import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  RefreshCw, 
  UserPlus, 
  Search, 
  AlertCircle, 
  Clock,
  Calendar,
  CalendarClock,
  Mail,
  BarChart3,
  LayoutGrid,
  LayoutList,
  Building,
  Phone,
  Edit,
  Trash2,
  CheckCircle,
} from 'lucide-react';
import axiosInstance from '../../lib/api/axiosInstance';
import { contactsApi } from '../../api/contacts';
import { networksApi } from '../../api/networks';
import { Button } from '../ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter,
} from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { format, differenceInDays } from 'date-fns';

// Importer les types du nouveau schéma
import { Contact, Network } from '../../types';

// Interface pour un prospect (Contact avec Network associé)
interface Prospect extends Contact {
  network?: Network;
  isProspect: boolean; // Flag pour identifier les prospects
}

// Interface pour le formulaire de création/édition de prospect
interface ProspectFormData {
  name: string;
  email: string;
  role: string;
  phone: string;
  notes: string;
  networkId: number | null;
}

// Interface pour les statistiques des prospects
interface ProspectStats {
  totalProspects: number;
  recentProspects: number;
  mediumProspects: number;
  oldProspects: number;
  roleDistribution: Record<string, number>;
  recentlyCreated: Prospect[];
}

export const ProspectsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [formData, setFormData] = useState<ProspectFormData>({
    name: '',
    email: '',
    role: '',
    phone: '',
    notes: '',
    networkId: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState({
    role: 'all',
    networkId: 'all',
  });
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('card');
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshClicked, setRefreshClicked] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Récupération des prospects (contacts avec networks)
  const fetchProspects = async () => {
    try {
      setLoading(true);
      setApiError(null);
      
      // Load contacts (prospects are contacts without a userId)
      const allContacts = await contactsApi.getAll();
      
      // Filtrer pour n'avoir que les prospects (contacts avec networkId mais pas userId)
      const prospectContacts = allContacts
        .filter(contact => contact.networkId && !contact.userId) // Prospects = contacts avec réseau mais sans compte utilisateur
        .map(contact => ({
          ...contact,
          isProspect: true,
        })) as Prospect[];
      
      setProspects(prospectContacts);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('Error fetching prospects:', error);
      const errorMessage = error.response?.data?.message || t('common.errorOccurred');
      setApiError(errorMessage);
      setProspects([]);
    } finally {
      setLoading(false);
    }
  };

  // Récupération des networks pour les sélecteurs
  const fetchNetworks = async () => {
    try {
      const response = await networksApi.getAll();
      setNetworks(response);
    } catch (error: any) {
      console.error('Error fetching networks:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshClicked(true);
    try {
      await Promise.all([fetchProspects(), fetchNetworks()]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshClicked(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchProspects(), fetchNetworks()]);
    
    // Refresh automatique toutes les 60 secondes
    const interval = setInterval(() => {
      fetchProspects();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const validateForm = (data: ProspectFormData) => {
    const newErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!data.name?.trim()) {
      newErrors.name = t('common.nameRequired');
    }

    if (!data.email?.trim()) {
      newErrors.email = t('common.emailRequired');
    } else if (!emailRegex.test(data.email)) {
      newErrors.email = t('common.invalidEmail');
    }

    if (!data.networkId) {
      newErrors.networkId = t('prospect.networkRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(formData)) {
      return;
    }

    try {
      const contactData = {
        name: formData.name,
        email: formData.email,
        role: formData.role || undefined,
        phone: formData.phone || undefined,
        notes: formData.notes || undefined,
        networkId: formData.networkId || undefined,
        userId: undefined, // Prospects n'ont pas de compte utilisateur
      };

      if (selectedProspect?.id) {
        await contactsApi.update(selectedProspect.id, contactData);
      } else {
        await contactsApi.create(contactData);
      }
      
      await fetchProspects();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving prospect:', error);
      const errorMessage = error.response?.data?.message || t('common.errorOccurred');
      setApiError(errorMessage);
    }
  };

  const handleDelete = async (prospect: Prospect) => {
    if (window.confirm(t('prospect.confirmDelete'))) {
      try {
        await contactsApi.delete(prospect.id);
        await fetchProspects();
      } catch (error) {
        console.error('Error deleting prospect:', error);
        alert(t('prospect.deleteError'));
      }
    }
  };
  
  const convertToUser = async (prospect: Prospect) => {
    if (window.confirm(t('prospect.confirmConvertToUser'))) {
      try {
        // Créer un utilisateur à partir du contact prospect
        const userData = {
          email: prospect.email,
          name: prospect.name,
          role: 'USER', // Role par défaut
          type: 'INDEPENDANT', // Type par défaut
          status: 'ACTIVE',
          // Note: Le firebaseId sera généré côté backend ou demandé à l'utilisateur
        };
        
        const userResponse = await axiosInstance.post('/users', userData);
        
        // Mettre à jour le contact pour l'associer au nouvel utilisateur
        await contactsApi.update(prospect.id, {
          userId: (userResponse.data as any).id,
        });
        
        await fetchProspects();
        alert(t('prospect.convertSuccess'));
      } catch (error) {
        console.error('Error converting prospect:', error);
        alert(t('prospect.convertError'));
      }
    }
  };

  const handleOpenDialog = (prospect?: Prospect) => {
    if (prospect) {
      setSelectedProspect(prospect);
      setFormData({
        name: prospect.name,
        email: prospect.email,
        role: prospect.role || '',
        phone: prospect.phone || '',
        notes: prospect.notes || '',
        networkId: prospect.networkId || null,
      });
    } else {
      setSelectedProspect(null);
      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        notes: '',
        networkId: null,
      });
    }
    setErrors({});
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedProspect(null);
    setFormData({
      name: '',
      email: '',
      role: '',
      phone: '',
      notes: '',
      networkId: null,
    });
    setErrors({});
  };

  const filterOptions = useMemo(() => {
    return {
      role: [...new Set(prospects.map(p => p.role).filter(Boolean))],
      networkId: [...new Set(prospects.map(p => p.networkId).filter(Boolean))]
    };
  }, [prospects]);

  // Filter prospects based on search query and other filters
  const filteredProspects = useMemo(() => {
    return prospects
      .filter(prospect => {
        // Filter by search query across multiple fields
        const matchesSearch = 
          searchQuery === "" ||
          prospect.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (prospect.role && prospect.role.toLowerCase().includes(searchQuery.toLowerCase())) ||
          prospect.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (prospect.network && prospect.network.name.toLowerCase().includes(searchQuery.toLowerCase()));
  
        // Apply dropdown filters
        const matchesFilters =
          (filters.role === 'all' || prospect.role === filters.role) &&
          (filters.networkId === 'all' || prospect.networkId?.toString() === filters.networkId);
  
        return matchesSearch && matchesFilters;
      })
      .sort((a, b) => {
        // Simple sort by creation date (newest first)
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [prospects, searchQuery, filters]);

  // Calculate prospect statistics
  const stats: ProspectStats = useMemo(() => {
    const prospectAges = prospects.map(prospect => {
      const createdAt = new Date(prospect.createdAt);
      const today = new Date();
      const ageInDays = differenceInDays(today, createdAt);
      
      if (ageInDays < 7) return 'recent';
      if (ageInDays < 30) return 'medium';
      return 'old';
    });
    
    const recentProspects = prospectAges.filter(age => age === 'recent').length;
    const mediumProspects = prospectAges.filter(age => age === 'medium').length;
    const oldProspects = prospectAges.filter(age => age === 'old').length;

    // Get distribution by role
    const roleDistribution = prospects.reduce((acc, prospect) => {
      const role = prospect.role || 'No Role';
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalProspects: prospects.length,
      recentProspects,
      mediumProspects,
      oldProspects,
      roleDistribution,
      recentlyCreated: prospects
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3)
    };
  }, [prospects]);

  if (loading && prospects.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              <CardTitle className="text-2xl">{t('prospect.management')}</CardTitle>
            </div>
            <CardDescription>{t('common.loading')}</CardDescription>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="space-y-4">
              <Skeleton className="h-[150px] w-full" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-primary" />
                  <CardTitle className="text-2xl">{t('prospect.management')}</CardTitle>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle>{t('common.error')}</AlertTitle>
              <AlertDescription>
                {apiError}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefresh}
                  className="ml-2"
                >
                  {t('common.retry')}
                </Button>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Main Status Card */}
      <Card className="border-none shadow-sm dark:shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary" />
                <CardTitle className="text-2xl">{t('prospect.management')}</CardTitle>
              </div>
              {lastUpdated && (
                <CardDescription className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {t('common.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
                </CardDescription>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={loading || refreshClicked}
                className="h-9 w-9 rounded-full transition-all"
                aria-label={t('common.refresh')}
              >
                <RefreshCw className={`h-4 w-4 ${(loading || refreshClicked) ? 'animate-spin text-primary' : ''}`} />
                <span className="sr-only">{t('common.refresh')}</span>
              </Button>
              <Button 
                onClick={() => handleOpenDialog()}
                className="h-9 bg-primary/90 hover:bg-primary transition-colors"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('prospect.addNew')}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-4">
          {/* Dashboard with statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Summary Cards */}
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('prospect.totalProspects')}</p>
                      <h3 className="text-3xl font-bold">{stats.totalProspects}</h3>
                    </div>
                    <UserPlus className="h-8 w-8 text-muted-foreground/60" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('prospect.recentProspects')}</p>
                      <h3 className="text-3xl font-bold">{stats.recentProspects}</h3>
                    </div>
                    <Calendar className="h-8 w-8 text-muted-foreground/60" />
                  </div>
                  <div className="mt-2">
                    <Progress 
                      value={(stats.recentProspects / Math.max(1, stats.totalProspects)) * 100} 
                      className="h-1 bg-muted/50"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('prospect.lastWeek')} ({Math.round((stats.recentProspects / Math.max(1, stats.totalProspects)) * 100)}%)
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/30 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-muted-foreground text-sm">{t('prospect.oldProspects')}</p>
                      <h3 className="text-3xl font-bold">{stats.oldProspects}</h3>
                    </div>
                    <Mail className="h-8 w-8 text-muted-foreground/60" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('prospect.needsFollowUp')}
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Prospect Role Distribution */}
            <div className="lg:col-span-3">
              <Card className="bg-muted/20 h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-muted-foreground" />
                    {t('prospect.roleDistribution')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {prospects.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                      <p>{t('prospect.noProspects')}</p>
                      <p className="text-sm">{t('prospect.addNewToSeeStats')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-2">
                      {/* Show all prospect roles with counts */}
                      <div className="space-y-3">
                        {Object.entries(stats.roleDistribution)
                          .sort(([, countA], [, countB]) => countB - countA)
                          .map(([role, count]) => (
                            <div key={role} className="flex items-center justify-between rounded-lg border p-2 text-sm">
                              <div className="flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-full ${getRoleColor(role)}`}></span>
                                <span className="font-medium">{role}</span>
                              </div>
                              <Badge variant="secondary">
                                {count}
                              </Badge>
                            </div>
                          ))}
                      </div>
                      
                      {/* Recent prospect highlights */}
                      {stats.recentlyCreated.length > 0 && (
                        <div className="mt-4">
                          <div className="p-3 bg-muted/40 rounded-lg">
                            <div className="flex items-center gap-2 text-sm">
                              <CalendarClock className="h-4 w-4 text-blue-500" />
                              <span className="text-muted-foreground">{t('prospect.recentlyCreated')}:</span>
                            </div>
                            {stats.recentlyCreated.map((prospect, index) => (
                              <React.Fragment key={prospect.id}>
                                <div className="flex items-center justify-between mt-1">
                                  <p className="font-medium">{prospect.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {format(new Date(prospect.createdAt), 'dd MMM yyyy')}
                                  </p>
                                </div>
                                {index < stats.recentlyCreated.length - 1 && (
                                  <Separator className="my-1" />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prospects List Card */}
      <Card className="border-none shadow-sm dark:shadow-lg overflow-hidden">
        <CardHeader className="pb-0">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl">{t('prospect.list')}</CardTitle>
            </div>
            <Tabs 
              value={viewMode} 
              onValueChange={(value) => setViewMode(value as "table" | "card")}
              className="w-full sm:w-auto"
            >
              <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="table" className="flex-1 sm:flex-initial flex items-center gap-2">
                  <LayoutList className="h-4 w-4" />
                  {t('prospect.tableView')}
                </TabsTrigger>
                <TabsTrigger value="card" className="flex-1 sm:flex-initial flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4" />
                  {t('prospect.cardView')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={t('prospect.searchPlaceholder')}
                className="pl-8 bg-muted/50 focus:bg-background transition-colors"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Select
                value={filters.role}
                onValueChange={(value) => setFilters({ ...filters, role: value })}
              >
                <SelectTrigger className="h-9 w-[150px] text-xs bg-background border-input">
                  <SelectValue placeholder={t('contact.role')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {filterOptions.role.map((role) => (
                    <SelectItem key={String(role)} value={String(role) || ''}>
                      {String(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.networkId}
                onValueChange={(value) => setFilters({ ...filters, networkId: value })}
              >
                <SelectTrigger className="h-9 w-[150px] text-xs bg-background border-input">
                  <SelectValue placeholder={t('network.network')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {networks.map((network) => (
                    <SelectItem key={network.id} value={network.id.toString()}>
                      {network.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {searchQuery && (
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                {t('prospect.foundResults', { found: filteredProspects.length, total: prospects.length })}
              </p>
            )}
          </div>

          <Tabs value={viewMode} className="mt-0">
            {/* Table View */}
            <TabsContent value="table" className="m-0 pt-4">
              <div className="bg-muted/20 backdrop-blur-[1px] rounded-lg border border-border/40 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium">{t('contact.name')}</th>
                        <th className="text-left p-3 font-medium">{t('contact.email')}</th>
                        <th className="text-left p-3 font-medium">{t('contact.role')}</th>
                        <th className="text-left p-3 font-medium">{t('network.network')}</th>
                        <th className="text-left p-3 font-medium">{t('common.createdAt')}</th>
                        <th className="text-left p-3 font-medium">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProspects.map((prospect) => (
                        <tr key={prospect.id} className="border-b hover:bg-muted/20 transition-colors">
                          <td className="p-3">
                            <div className="font-medium">{prospect.name}</div>
                          </td>
                          <td className="p-3">
                            <div className="text-muted-foreground">{prospect.email}</div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{prospect.role || 'No Role'}</Badge>
                          </td>
                          <td className="p-3">
                            <div className="text-muted-foreground">
                              {prospect.network?.name || 'No Network'}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="text-muted-foreground text-sm">
                              {format(new Date(prospect.createdAt), 'dd MMM yyyy')}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenDialog(prospect)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => convertToUser(prospect)}
                                className="bg-green-500/10 text-green-700 border-green-500/20 hover:bg-green-500/20"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(prospect)}
                                className="bg-red-500/10 text-red-700 border-red-500/20 hover:bg-red-500/20"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
            
            {/* Card View */}
            <TabsContent value="card" className="m-0 pt-4">
              {filteredProspects.length === 0 ? (
                <div className="text-center py-10 border border-dashed rounded-lg">
                  <UserPlus className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
                  <h3 className="text-lg font-medium mb-1">{t('prospect.noProspectsFound')}</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-4">
                    {searchQuery ? t('prospect.tryDifferentSearch') : t('prospect.addFirstProspect')}
                  </p>
                  <Button onClick={() => handleOpenDialog()}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('prospect.addNew')}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredProspects.map(prospect => (
                    <Card key={prospect.id} className="overflow-hidden border hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between">
                          <CardTitle className="truncate text-base">{prospect.name}</CardTitle>
                          <Badge variant="outline" className="text-xs">
                            {prospect.role || 'No Role'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="truncate">{prospect.email}</span>
                          </div>
                          
                          {prospect.phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-3.5 w-3.5" />
                              <span>{prospect.phone}</span>
                            </div>
                          )}
                          
                          {prospect.network && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Building className="h-3.5 w-3.5" />
                              <span>{prospect.network.name}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>
                              {format(new Date(prospect.createdAt), 'PP')}
                            </span>
                          </div>
                          
                          {prospect.notes && (
                            <div className="pt-1">
                              <p className="text-xs text-muted-foreground mb-1">{t('contact.notes')}:</p>
                              <p className="text-xs bg-muted/30 p-2 rounded">
                                {prospect.notes.length > 100 
                                  ? `${prospect.notes.substring(0, 100)}...` 
                                  : prospect.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                      <CardFooter className="justify-between pt-1 pb-3 gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleOpenDialog(prospect)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t('common.edit')}
                        </Button>
                        <div className="space-x-1">
                          <Button 
                            onClick={() => convertToUser(prospect)}
                            size="sm"
                            variant="outline"
                            className="bg-green-500/10 text-green-700 border-green-500/20 hover:bg-green-500/20"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {t('prospect.convertToUser')}
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Prospect Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedProspect ? t('prospect.editProspect') : t('prospect.addProspect')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('contact.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('contact.namePlaceholder')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('contact.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('contact.emailPlaceholder')}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t('contact.role')}</Label>
              <Input
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder={t('contact.rolePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('contact.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('contact.phonePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="networkId">{t('network.network')} *</Label>
              <Select
                value={formData.networkId?.toString() || ''}
                onValueChange={(value) => setFormData({ ...formData, networkId: value ? parseInt(value) : null })}
              >
                <SelectTrigger className={errors.networkId ? 'border-red-500' : ''}>
                  <SelectValue placeholder={t('network.selectNetwork')} />
                </SelectTrigger>
                <SelectContent>
                  {networks.map((network) => (
                    <SelectItem key={network.id} value={network.id.toString()}>
                      {network.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.networkId && <p className="text-red-500 text-xs">{errors.networkId}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('contact.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('contact.notesPlaceholder')}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('common.cancel')}
              </Button>
              <Button type="submit">
                {selectedProspect ? t('common.update') : t('common.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProspectsManagement;

/**
 * Helper function to get role color class
 */
function getRoleColor(role: string): string {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
    'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500'
  ];
  
  // Génère une couleur basée sur le hash du role
  let hash = 0;
  for (let i = 0; i < role.length; i++) {
    hash = role.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}
